import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { 
  Crown, Check, Star, Users, Calendar, Bell, Film, BookOpen, 
  ArrowLeft, Loader, Sparkles, Gift, ChevronRight
} from "lucide-react";

export default function SubscriptionPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState("annual");
  const [processing, setProcessing] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    loadData();
    // Check if returning from Stripe
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, []);

  const loadData = async () => {
    try {
      const [plansRes, subRes] = await Promise.all([
        axios.get(`${API}/subscription/plans`),
        axios.get(`${API}/subscription/status`)
      ]);
      setPlans(plansRes.data);
      setSubscription(subRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId, attempts = 0) => {
    setCheckingPayment(true);
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setCheckingPayment(false);
      return;
    }

    try {
      const { data } = await axios.get(`${API}/subscription/checkout/status/${sessionId}`);
      
      if (data.payment_status === "paid") {
        await loadData();
        setCheckingPayment(false);
        return;
      }
      
      // Keep polling
      setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (e) {
      console.error(e);
      setCheckingPayment(false);
    }
  };

  const handleStartTrial = async () => {
    setProcessing(true);
    try {
      await axios.post(`${API}/subscription/start-trial`);
      await loadData();
      navigate("/parent");
    } catch (e) {
      alert(e.response?.data?.detail || "Could not start trial");
    } finally {
      setProcessing(false);
    }
  };

  const handleSubscribe = async () => {
    setProcessing(true);
    try {
      const { data } = await axios.post(`${API}/subscription/checkout`, {
        plan_id: selectedPlan,
        origin_url: window.location.origin
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      alert(e.response?.data?.detail || "Could not start checkout");
      setProcessing(false);
    }
  };

  if (loading || checkingPayment) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#F0F4FF" }}>
        <div className="text-center">
          <Loader size={48} className="mx-auto animate-spin mb-4" color="#9B5DE5" />
          <p className="font-bold text-[#073B4C]">
            {checkingPayment ? "Confirming your payment..." : "Loading..."}
          </p>
        </div>
      </div>
    );
  }

  // Already premium
  if (subscription?.is_premium) {
    return (
      <div className="min-h-screen" style={{ background: "#F0F4FF" }}>
        <div className="px-4 pt-6 pb-10"
          style={{ background: "linear-gradient(135deg, #9B5DE5 0%, #EF476F 100%)" }}>
          <div className="max-w-2xl mx-auto">
            <button onClick={() => navigate("/parent")}
              className="flex items-center gap-1.5 text-white/80 font-bold text-sm bg-white/20 px-3 py-1.5 rounded-xl mb-4">
              <ArrowLeft size={16} /> Back
            </button>
            <div className="text-center">
              <Crown size={48} color="#FFD166" className="mx-auto mb-3" />
              <h1 className="text-3xl font-black text-white mb-2" style={{ fontFamily: "Fredoka, sans-serif" }}>
                You're Premium!
              </h1>
              <p className="text-white/80 font-medium">
                {subscription.plan === "trial" 
                  ? `Free trial ends ${new Date(subscription.trial_ends).toLocaleDateString()}`
                  : `${subscription.plan === "annual" ? "Annual" : "Monthly"} plan`}
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 -mt-5">
          <div className="bg-white rounded-3xl shadow-xl p-6">
            <h2 className="font-black text-[#073B4C] mb-4" style={{ fontFamily: "Fredoka, sans-serif" }}>
              Your Premium Features
            </h2>
            <div className="space-y-3">
              {plans?.premium_features?.map((feature, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-green-50">
                  <Check size={20} color="#06D6A0" strokeWidth={3} />
                  <span className="font-semibold text-[#073B4C]">{feature}</span>
                </div>
              ))}
            </div>

            {subscription.plan === "trial" && (
              <div className="mt-6 p-4 rounded-2xl border-2 border-dashed border-[#9B5DE5] bg-purple-50">
                <p className="font-bold text-[#9B5DE5] mb-3">
                  Love Bible Buds? Subscribe to keep all features!
                </p>
                <button onClick={() => setSubscription({ ...subscription, is_premium: false })}
                  className="w-full text-white font-black py-3 rounded-xl"
                  style={{ background: "linear-gradient(90deg, #9B5DE5, #EF476F)" }}>
                  View Plans
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-8" style={{ background: "#F0F4FF" }}>
      {/* Header */}
      <div className="px-4 pt-6 pb-12"
        style={{ background: "linear-gradient(135deg, #9B5DE5 0%, #EF476F 50%, #FFD166 100%)" }}>
        <div className="max-w-2xl mx-auto">
          <button onClick={() => navigate("/parent")}
            className="flex items-center gap-1.5 text-white/80 font-bold text-sm bg-white/20 px-3 py-1.5 rounded-xl mb-6">
            <ArrowLeft size={16} /> Back
          </button>
          <div className="text-center">
            <div className="bg-white/20 rounded-full p-4 w-20 h-20 flex items-center justify-center mx-auto mb-4">
              <Crown size={40} color="white" strokeWidth={2.5} />
            </div>
            <h1 className="text-3xl md:text-4xl font-black text-white mb-2"
              style={{ fontFamily: "Fredoka, sans-serif", textShadow: "0 2px 10px rgba(0,0,0,0.2)" }}>
              Unlock Premium
            </h1>
            <p className="text-white/90 font-semibold">
              Get the full Bible Buds experience for your family
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 -mt-6">
        {/* Trial Info Banner */}
        <div className="bg-white rounded-3xl shadow-xl p-5 mb-5 border-2 border-[#06D6A0]">
          <div className="flex items-center gap-4">
            <div className="bg-green-100 rounded-2xl p-3">
              <Gift size={28} color="#06D6A0" strokeWidth={2.5} />
            </div>
            <div className="flex-1">
              <p className="font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
                7-Day Free Trial
              </p>
              <p className="text-gray-500 text-sm font-medium">
                Try all premium features free for 7 days. You'll be charged after trial ends.
              </p>
            </div>
          </div>
        </div>

        {/* Plan Selection */}
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden mb-5">
          <div className="p-5 border-b" style={{ background: "linear-gradient(90deg, #F0E6FF, #FFE0EB)" }}>
            <h2 className="font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
              Choose Your Plan
            </h2>
          </div>
          
          <div className="p-4 space-y-3">
            {plans?.plans?.map((plan) => (
              <button key={plan.id}
                onClick={() => setSelectedPlan(plan.id)}
                className={`w-full p-4 rounded-2xl border-2 transition-all text-left ${
                  selectedPlan === plan.id 
                    ? "border-[#9B5DE5] bg-purple-50" 
                    : "border-gray-200 hover:border-gray-300"
                }`}
                data-testid={`plan-${plan.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      selectedPlan === plan.id ? "border-[#9B5DE5] bg-[#9B5DE5]" : "border-gray-300"
                    }`}>
                      {selectedPlan === plan.id && <Check size={12} color="white" strokeWidth={3} />}
                    </div>
                    <div>
                      <p className="font-black text-[#073B4C]">{plan.name}</p>
                      <p className="text-xs text-gray-500 font-medium">{plan.description}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-black text-xl text-[#073B4C]">${plan.price}</p>
                    <p className="text-xs text-gray-400">/{plan.interval}</p>
                  </div>
                </div>
                {plan.savings && (
                  <div className="mt-2 inline-block bg-green-100 text-green-700 text-xs font-black px-2 py-1 rounded-full">
                    SAVE {plan.savings}
                  </div>
                )}
              </button>
            ))}
          </div>

          <div className="p-4 border-t">
            <button onClick={handleSubscribe}
              disabled={processing}
              className="w-full text-white font-black py-4 rounded-2xl text-lg disabled:opacity-50"
              style={{ background: "linear-gradient(90deg, #9B5DE5, #EF476F)" }}
              data-testid="subscribe-btn">
              {processing ? "Processing..." : `Start 7-Day Free Trial`}
            </button>
            <p className="text-center text-xs text-gray-400 mt-2">
              Then ${plans?.plans?.find(p => p.id === selectedPlan)?.price}/{selectedPlan === "annual" ? "year" : "month"} · Cancel anytime
            </p>
          </div>
        </div>

        {/* Features Comparison */}
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          <div className="p-5 border-b" style={{ background: "linear-gradient(90deg, #E0F4FF, #E0FFF5)" }}>
            <h2 className="font-black text-[#073B4C]" style={{ fontFamily: "Fredoka, sans-serif" }}>
              What You Get
            </h2>
          </div>

          <div className="p-4">
            <div className="mb-4">
              <p className="text-xs font-black text-gray-400 uppercase tracking-wider mb-2">Free Features</p>
              {plans?.free_features?.map((feature, i) => (
                <div key={i} className="flex items-center gap-2 py-1.5">
                  <Check size={16} color="#06D6A0" strokeWidth={3} />
                  <span className="text-sm text-gray-600 font-medium">{feature}</span>
                </div>
              ))}
            </div>

            <div className="pt-4 border-t">
              <p className="text-xs font-black text-[#9B5DE5] uppercase tracking-wider mb-2 flex items-center gap-1">
                <Sparkles size={12} /> Premium Features
              </p>
              {plans?.premium_features?.map((feature, i) => (
                <div key={i} className="flex items-center gap-2 py-1.5">
                  <Star size={16} color="#FFD166" fill="#FFD166" strokeWidth={0} />
                  <span className="text-sm text-[#073B4C] font-semibold">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
